import React from "react"

import { format, parseISO } from "date-fns"
import { Calendar, Clock, Info, Loader2 } from "lucide-react"
import { toast } from "sonner"

import ShiftAuditDrawer from "@/components/sheriff/shift/ShiftAuditDrawer"
import ShiftChangesButton from "@/components/sheriff/shift/ShiftChangesButton"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { INVALID_UK_TIME_ERROR, ukWallClockToInstant } from "@/constants/sheriffOptions"
import { useAuth } from "@/contexts/AuthContext"
import { useDepots } from "@/hooks/useDepots"
import { useShiftChangesPanel } from "@/hooks/useShiftChangesPanel"
import { cn } from "@/lib/utils"
import { type CancelledReason } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { selectableDepots } from "@/utils/depotValidity"
import { formatPersonShortName } from "@/utils/personDisplay"

const NO_CANCEL_REASON = "__none__"

const CANCEL_REASON_OPTIONS: Array<{ value: CancelledReason; label: string }> = [
  { value: "Sickness", label: "Sickness" },
  { value: "FamilyEmergency", label: "Family Emergency" },
  { value: "OtherWorkCommitments", label: "Other Work Commitments" },
  { value: "HivedCapacity", label: "Hived Capacity" },
  { value: "RetailerRequest", label: "Retailer Request" },
  { value: "Offboarded", label: "Offboarded" },
  { value: "RequestedByDriver", label: "Requested By Driver" },
  { value: "RequestedByDsp", label: "Requested by DSP" },
  { value: "UnavailabilityRequest", label: "Unavailability Request" },
  { value: "MovedPosition", label: "Moved Position" },
  { value: "Onboarded", label: "Onboarded" },
  { value: "SheriffTypeChanged", label: "Sheriff Type Changed" },
]

export interface BlockDetails {
  id: string
  startDate: string // ISO date yyyy-mm-dd or ISO string
  endDate: string // ISO date yyyy-mm-dd or ISO string
  startTime?: string // HH:mm
  endTime?: string // HH:mm
  realStartTime?: string // HH:mm
  realEndTime?: string // HH:mm
  area: string
  type: string
  role: string
  location: string
  notes?: string
  cancelReason?: CancelledReason
}

interface BlockDetailsDialogProps {
  mode: "view" | "edit"
  block: BlockDetails | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave?: (updated: BlockDetails) => void
  person: Person
}

const normalizeDateInput = (date: string) => {
  try {
    const parsedDate = new Date(date)
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().slice(0, 10)
    }
  } catch {
    // Ignore parsing errors
  }

  return date.slice(0, 10)
}

const getDurationLabel = (start?: string, end?: string) => {
  if (!start || !end) return ""

  const [startHours, startMinutes] = start.split(":").map(Number)
  const [endHours, endMinutes] = end.split(":").map(Number)

  if ([startHours, startMinutes, endHours, endMinutes].some((value) => Number.isNaN(value))) {
    return ""
  }

  const startTotalMinutes = startHours * 60 + startMinutes
  let endTotalMinutes = endHours * 60 + endMinutes
  if (endTotalMinutes < startTotalMinutes) endTotalMinutes += 24 * 60

  const totalMinutes = Math.max(0, endTotalMinutes - startTotalMinutes)
  if (!totalMinutes) return ""

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return ` (${hours}h${minutes ? ` ${minutes}m` : ""})`
}

const BlockDetailsDialog: React.FC<BlockDetailsDialogProps> = ({
  mode,
  block,
  open,
  onOpenChange,
  onSave,
  person,
}) => {
  const { data: depots, isLoading: depotsLoading } = useDepots()
  const { user } = useAuth()
  const isView = mode === "view"
  const canViewAudit = user?.role === "Admin" || user?.role === "Manager"
  const [isSaving, setIsSaving] = React.useState(false)
  const [formData, setFormData] = React.useState<BlockDetails | null>(block)
  const { changesOpen: auditOpen, setChangesOpen: setAuditOpen } = useShiftChangesPanel({
    recordId: block?.id,
    open,
  })

  const availableDepots = React.useMemo(() => {
    const allowedLocations = new Set(
      (person.location || []).map((location) => location.toLowerCase()),
    )

    // Keep the block's own location even if that depot has since closed, so it can still be edited
    return selectableDepots(
      depots || [],
      formData?.startDate.slice(0, 10) ?? "",
      formData?.endDate.slice(0, 10) ?? "",
      (depot) => depot.name === block?.location,
    ).filter((depot) => allowedLocations.has(depot.name.toLowerCase()))
  }, [depots, person.location, formData?.startDate, formData?.endDate, block?.location])
  const personAreas = person.sheriff_areas ?? []

  React.useEffect(() => {
    if (!block) {
      setFormData(null)
      return
    }

    setFormData({
      ...block,
      startDate: normalizeDateInput(block.startDate),
      endDate: normalizeDateInput(block.endDate),
    })
  }, [block])

  if (!formData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-palette-jungleGreen600">No block selected.</div>
        </DialogContent>
      </Dialog>
    )
  }

  const displayedType = formData.type || person.sheriff_type || ""
  const displayedRole = formData.role || person.employer || "No role on person"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isView) {
      onOpenChange(false)
      return
    }

    const isCancelling = Boolean(formData.cancelReason)

    if (!isCancelling) {
      if (!formData.startDate || !formData.endDate) {
        toast.error("Please select start and end date")
        return
      }
      if (!formData.startTime || !formData.endTime) {
        toast.error("Please select start and end time")
        return
      }

      if (personAreas.length === 0) {
        toast.error("No sheriff areas on person")
        return
      }
      if (!personAreas.includes(formData.area as (typeof personAreas)[number])) {
        toast.error("Please select an area from person profile")
        return
      }

      if (availableDepots.length === 0) {
        toast.error("No locations on person")
        return
      }
      if (!availableDepots.some((depot) => depot.name === formData.location)) {
        toast.error("Please select a location from person profile")
        return
      }

      const startDateTime = ukWallClockToInstant(formData.startDate, formData.startTime)
      const endDateTime = ukWallClockToInstant(formData.endDate, formData.endTime)

      if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        toast.error(INVALID_UK_TIME_ERROR)
        return
      }

      if (startDateTime > endDateTime) {
        toast.error("End date/time must be after start date/time")
        return
      }
    }

    setIsSaving(true)
    try {
      await onSave?.(formData)
      onOpenChange(false)
    } catch (error) {
      console.error("Failed to save:", error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[calc(100vw-2rem)] overflow-hidden p-0",
          auditOpen && canViewAudit ? "max-w-[1100px]" : "max-w-2xl",
        )}
      >
        <DialogHeader className="border-b px-6 py-5 pr-20">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl font-bold">
              {isView ? "View Block" : "Edit Block"}
            </DialogTitle>
            {canViewAudit ? (
              <ShiftChangesButton
                open={auditOpen}
                onClick={() => setAuditOpen((current) => !current)}
              />
            ) : null}
          </div>
        </DialogHeader>
        <div className="relative flex h-[70vh] max-h-[760px] min-h-[420px]">
          <div className="min-w-0 flex flex-1 flex-col overflow-hidden">
            {isView ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 pb-4 pt-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Person</Label>
                      <div className="mt-1 text-sm">{formatPersonShortName(person)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <div className="mt-1 text-sm">
                        {format(parseISO(formData.startDate), "PPP")}
                      </div>
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <div className="mt-1 text-sm">
                        {format(parseISO(formData.endDate), "PPP")}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Start Time (UK)</Label>
                      <div className="mt-1 text-sm">{formData.startTime || "-"}</div>
                    </div>
                    <div>
                      <Label>End Time (UK)</Label>
                      <div className="mt-1 text-sm">{formData.endTime || "-"}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Area</Label>
                      <div className="mt-1 text-sm">{formData.area || "-"}</div>
                    </div>
                    <div>
                      <Label>Type</Label>
                      <div className="mt-1 text-sm">{displayedType || "-"}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Role</Label>
                      <div className="mt-1 text-sm">{displayedRole}</div>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <div className="mt-1 text-sm">{formData.location || "-"}</div>
                    </div>
                  </div>

                  <div>
                    <Label>Block Notes</Label>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{formData.notes || ""}</div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-md p-3 bg-palette-jungleGreen000">
                      <div className="text-sm font-medium mb-1">Scheduled</div>
                      <div className="text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-palette-jungleGreen500" />
                        <span>
                          {(formData.startTime || "-") + " - " + (formData.endTime || "-")}
                          {getDurationLabel(formData.startTime, formData.endTime)}
                        </span>
                      </div>
                    </div>
                    <div className="border rounded-md p-3 bg-palette-jungleGreen000">
                      <div className="text-sm font-medium mb-1">Clocked</div>
                      <div className="text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-palette-jungleGreen500" />
                        <span>
                          {(formData.realStartTime || "-") + " - " + (formData.realEndTime || "-")}
                          {getDurationLabel(formData.realStartTime, formData.realEndTime)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 pb-4 pt-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Person</Label>
                      <div className="text-sm">{formatPersonShortName(person)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.startDate && "text-muted-foreground",
                            )}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formData.startDate ? (
                              format(parseISO(formData.startDate), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                          <CalendarComponent
                            mode="single"
                            selected={formData.startDate ? parseISO(formData.startDate) : undefined}
                            onSelect={(date) =>
                              date &&
                              setFormData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      startDate: format(date, "yyyy-MM-dd"),
                                    }
                                  : prev,
                              )
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Start Time (UK)</Label>
                      <div className="relative">
                        <Input
                          id="startTime"
                          type="time"
                          value={formData.startTime || ""}
                          onChange={(e) =>
                            setFormData((prev) =>
                              prev ? { ...prev, startTime: e.target.value } : prev,
                            )
                          }
                          className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.endDate && "text-muted-foreground",
                            )}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formData.endDate ? (
                              format(parseISO(formData.endDate), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                          <CalendarComponent
                            mode="single"
                            selected={formData.endDate ? parseISO(formData.endDate) : undefined}
                            onSelect={(date) =>
                              date &&
                              setFormData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      endDate: format(date, "yyyy-MM-dd"),
                                    }
                                  : prev,
                              )
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endTime">End Time (UK)</Label>
                      <div className="relative">
                        <Input
                          id="endTime"
                          type="time"
                          value={formData.endTime || ""}
                          onChange={(e) =>
                            setFormData((prev) =>
                              prev ? { ...prev, endTime: e.target.value } : prev,
                            )
                          }
                          className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="area">Area</Label>
                      <Select
                        value={formData.area}
                        onValueChange={(value) =>
                          setFormData((prev) => (prev ? { ...prev, area: value } : prev))
                        }
                        disabled={personAreas.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              personAreas.length === 0
                                ? "No sheriff areas on person"
                                : "Select an area"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {personAreas.map((area) => (
                            <SelectItem key={area} value={area}>
                              {area}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="type">Type</Label>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Type info"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="start"
                              sideOffset={6}
                              className="max-w-[220px]"
                            >
                              This is the person&apos;s sheriff type from their profile. Update it
                              on the person record if it needs to change.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input id="type" value={displayedType} disabled readOnly />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="role">Role</Label>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Role info"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="start"
                              sideOffset={6}
                              className="max-w-[220px]"
                            >
                              This is the person&apos;s role from their profile. Update it on the
                              person record if it needs to change.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input id="role" value={displayedRole} disabled readOnly />
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Select
                        value={formData.location}
                        onValueChange={(value) =>
                          setFormData((prev) => (prev ? { ...prev, location: value } : prev))
                        }
                        disabled={depotsLoading || availableDepots.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              depotsLoading
                                ? "Loading locations..."
                                : availableDepots.length === 0
                                  ? "No locations on person"
                                  : "Select a location"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDepots.map((depot) => (
                            <SelectItem key={depot.id} value={depot.name}>
                              {depot.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Block Notes</Label>
                    <Input
                      id="notes"
                      placeholder="Add any notes about this block"
                      value={formData.notes || ""}
                      onChange={(e) =>
                        setFormData((prev) => (prev ? { ...prev, notes: e.target.value } : prev))
                      }
                    />
                  </div>

                  <div className="space-y-2 mb-4">
                    <Label htmlFor="cancel" className="text-red-600">
                      Cancel Block
                    </Label>
                    <Select
                      value={formData.cancelReason ?? NO_CANCEL_REASON}
                      onValueChange={(value) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                cancelReason:
                                  value === NO_CANCEL_REASON
                                    ? undefined
                                    : (value as CancelledReason),
                              }
                            : prev,
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_CANCEL_REASON}>Do not cancel</SelectItem>
                        {CANCEL_REASON_OPTIONS.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 border-t bg-palette-jungleGreen000">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
          <ShiftAuditDrawer
            shiftId={formData.id}
            open={auditOpen}
            canView={canViewAudit}
            onClose={() => setAuditOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BlockDetailsDialog
